import {
  AdjustmentMode,
  BoundOperator,
  getHigherSpecEffect,
  HigherSpecEffect,
  ProductPriceBasisType,
  QualityFormulaType,
  TierMode,
} from '@local/lib';

import type { SpecDefinition, SpecState } from './qualityFormulaTypes';

import { CONTRACT_PRICE_COMPONENTS, getSpecUnit } from './qualityFormulaMetadata';

export type PlaygroundStatus = 'danger' | 'success' | 'warning';

export interface PlaygroundRow {
  equation: string;
  impact: number;
  kind: 'price' | 'quantity';
  spec: SpecDefinition;
  status: PlaygroundStatus;
}

export const getContractValue = (state: SpecState): number => {
  if (state.formulaType === QualityFormulaType.Percentage || state.formulaType === QualityFormulaType.PerUnit)
    return state.contractedSpec;
  const row = state.rows[0];
  if (state.formulaType === QualityFormulaType.AdjustedSalesPrice && 'contractedSpec' in row) return row.contractedSpec;
  if ('lowerBound' in row) return row.lowerBound;
  throw new Error(`Missing contracted value for formula ${state.formulaType}`);
};

export const getInputError = (value: string, percent = false): string | undefined => {
  if (value.trim() === '' || !Number.isFinite(Number(value))) return 'Enter a number.';
  if (Number(value) < 0) return 'Cannot be negative.';
  if (percent && Number(value) > 100) return 'Enter a percentage from 0 to 100.';
  return undefined;
};

export const getPlaygroundStatus = (spec: SpecDefinition, state: SpecState, actual: number): PlaygroundStatus => {
  if (state.formulaType === QualityFormulaType.QuantityAdjustedByPercent) return 'success';
  const contract = getContractValue(state);
  const worseAbove = getHigherSpecEffect(spec.key) === HigherSpecEffect.Discount;
  const deviation = worseAbove ? actual - contract : contract - actual;
  if (deviation <= 0) return 'success';
  return deviation <= Math.max(Math.abs(contract) * 0.1, 0.01) ? 'warning' : 'danger';
};

export interface PlaygroundInputs {
  actual: Record<string, string>;
  components: Record<string, Record<string, string>>;
  price: string;
  quantity: string;
}

// Illustrative client-side estimate only; the pricing engine is not available in this app.
// Keep this separate from calculateQualityAdjustments so the mock API contract stays intact.
export const previewPlayground = (
  specs: SpecDefinition[],
  states: Record<string, SpecState>,
  inputs: PlaygroundInputs,
): { finalPrice: number; finalQuantity: number; rows: PlaygroundRow[] } => {
  const price = Number(inputs.price);
  const quantity = Number(inputs.quantity);
  let finalPrice = price;
  let finalQuantity = quantity;
  const rows: PlaygroundRow[] = [];

  for (const spec of specs.filter(item => {
    const state = states[item.key];
    if (getInputError(inputs.actual[item.key], getSpecUnit(item.key) === '%')) return false;
    if (
      state.formulaType !== QualityFormulaType.Percentage ||
      state.priceBasis.type !== ProductPriceBasisType.PriceComponents
    )
      return true;
    return CONTRACT_PRICE_COMPONENTS.every(component => !getInputError(inputs.components[item.key][component.key]));
  })) {
    const state = states[spec.key];
    const value = inputs.actual[spec.key];
    const actual = Number(value);
    const contract = getContractValue(state);
    let impact = 0;
    let kind: PlaygroundRow['kind'] = 'price';
    let equation = 'No adjustment';

    if (state.formulaType === QualityFormulaType.QuantityAdjustedByPercent) {
      kind = 'quantity';
      const adjusted = (quantity * actual) / 100;
      impact = adjusted - quantity;
      equation = `${quantity} MT × ${actual}% = ${adjusted.toFixed(2)} MT`;
    } else if (
      state.formulaType === QualityFormulaType.QuantityAdjustedToContracted ||
      state.formulaType === QualityFormulaType.QuantityAdjustedToDry
    ) {
      kind = 'quantity';
      let factor = 1;
      if (actual > contract) {
        factor =
          state.formulaType === QualityFormulaType.QuantityAdjustedToDry
            ? (100 - actual) / (100 - contract)
            : contract / actual;
      }
      const adjusted = quantity * factor;
      impact = adjusted - quantity;
      let factorText = '1';
      if (adjusted !== quantity) {
        factorText =
          state.formulaType === QualityFormulaType.QuantityAdjustedToDry
            ? `(${100 - actual} ÷ ${100 - contract})`
            : `(${contract} ÷ ${actual})`;
      }
      equation = `${quantity} MT × ${factorText} = ${adjusted.toFixed(2)} MT`;
    } else if (state.formulaType === QualityFormulaType.AdjustedSalesPrice) {
      const adjusted = contract ? (price * actual) / contract : price;
      impact = adjusted - price;
      equation = `$${price.toFixed(2)} × (${actual} ÷ ${contract}) = $${adjusted.toFixed(2)}/MT`;
    } else {
      const worseAbove = getHigherSpecEffect(spec.key) === HigherSpecEffect.Discount;
      const isDiscount = worseAbove ? actual > contract : actual < contract;
      const mode = isDiscount ? AdjustmentMode.Discount : AdjustmentMode.Premium;
      const row =
        state.tierMode === TierMode.Threshold
          ? state.rows.find(candidate => 'adjustmentMode' in candidate && candidate.adjustmentMode === mode)
          : state.rows.find(candidate => {
              if (!('lowerBoundOperator' in candidate)) return false;
              return (
                (candidate.lowerBoundOperator === BoundOperator.Inclusive
                  ? actual >= candidate.lowerBound
                  : actual > candidate.lowerBound) &&
                (candidate.upperBound === null ||
                  (candidate.upperBoundOperator === BoundOperator.Inclusive
                    ? actual <= candidate.upperBound
                    : actual < candidate.upperBound))
              );
            });
      if (row && 'perIncrement' in row && actual !== contract) {
        let basis = price;
        if (state.formulaType === QualityFormulaType.Percentage) {
          if (state.priceBasis.type === ProductPriceBasisType.DeemedPrice) basis = state.priceBasis.deemedPrice;
          if (state.priceBasis.type === ProductPriceBasisType.PriceComponents) {
            basis = CONTRACT_PRICE_COMPONENTS.reduce(
              (total, component) => total + component.sign * Number(inputs.components[spec.key][component.key]),
              0,
            );
          }
        }
        const rate =
          state.formulaType === QualityFormulaType.Percentage
            ? (basis * row.percentFactor) / 100
            : row.qualityPricePerUnit;
        const increments = row.isFlatAdjustment ? 1 : Math.abs(actual - contract) / row.perIncrement;
        impact = (isDiscount ? -1 : 1) * rate * increments;
        equation = `${state.formulaType === QualityFormulaType.Percentage ? `$${basis.toFixed(2)} × ${row.percentFactor}%` : `$${rate.toFixed(2)}`} × ${increments.toFixed(2)} = ${impact < 0 ? '−' : ''}$${Math.abs(impact).toFixed(2)}/MT`;
      }
    }

    if (kind === 'price') finalPrice += impact;
    else finalQuantity += impact;
    rows.push({ equation, impact, kind, spec, status: getPlaygroundStatus(spec, state, actual) });
  }
  return { finalPrice, finalQuantity, rows };
};
